import { useTheme } from '@mui/material';
import type { CardProps as MuiCardProps } from '@mui/material';
import type { ReactNode } from 'react';

import {
  type ResourceColorName,
  type ResourceColorPalette,
  resourcesColors,
} from '../../../theme/tokens/resourcesColors';
import { resourcesIcons } from '../../../theme/tokens/resourcesIcons';
import type { WithoutStyleOverrides } from '../../../types/WithoutStyleOverrides';
import { ResourceAvatar } from './ResourceAvatar';

export type CardResourceVariant = ResourceColorName;

type Props = WithoutStyleOverrides<Omit<MuiCardProps, 'variant' | 'elevation' | 'square' | 'raised' | 'children'>> & {
  variant: CardResourceVariant;
  title?: ReactNode;
  subheader?: ReactNode;
  action?: ReactNode;
  avatar?: ReactNode;
  children?: ReactNode;
  disableCardContent?: boolean;
  fullHeight?: boolean;
};

export function useCardResource(props: Props) {
  const theme = useTheme();

  const paletteColor = theme.palette[props.variant]
    ?? theme.palette.resourcesColors?.[props.variant]
    ?? resourcesColors[props.variant];

  const resourceColor: ResourceColorPalette = {
    main: paletteColor?.main ?? resourcesColors[props.variant]?.main ?? '#616161',
    light: paletteColor?.light ?? resourcesColors[props.variant]?.light ?? '#f5f5f5',
    dark: paletteColor?.dark ?? resourcesColors[props.variant]?.dark ?? '#212121',
    contrastText: paletteColor?.contrastText ?? resourcesColors[props.variant]?.contrastText ?? '#ffffff',
  };

  const IconComponent = theme.resourceIcons?.[props.variant] ?? resourcesIcons[props.variant];

  const avatar = props.avatar !== undefined
    ? props.avatar
    : (
        IconComponent ? (
          <ResourceAvatar resourceColor={resourceColor}>
            <IconComponent />
          </ResourceAvatar>
        ) : null
      );

  return {
    resourceColor,
    IconComponent,
    avatar,
  };
}

export type { Props as CardResourceProps };
